import { useListContext } from 'react-admin';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { isAuthorizationError } from '@src/apis/reware/utils';

/**
 * What the Gift cards list renders instead of a datagrid when the query fails.
 *
 * The denial case is called out separately because it is reachable by ordinary configuration, not
 * only by breakage. The sidebar entry is gated on `config_portal_app_catalog` while the server
 * gates the data on `admin_gift_cards_portal`, and the client never receives the latter — login
 * keeps only `config_portal_app_*` permissions. Anyone with catalog access therefore sees this
 * page in the nav whether or not they may read gift cards, and the broad grant makes that the
 * likely case rather than the rare one. Left to react-admin's default the page renders an empty
 * datagrid over an error toast, which reads as "this partner has no gift cards".
 */
const GiftcardListError = () => {
  const { error } = useListContext();

  if (isAuthorizationError(error)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <AlertTitle>You don&apos;t have access to gift cards</AlertTitle>
          <Typography variant="body2">
            Your account can reach this page but isn&apos;t permitted to read gift card data for
            this partner. Ask your Trove representative for gift card portal access, or switch to
            a partner where you already have it.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">
        <AlertTitle>Couldn&apos;t load gift cards</AlertTitle>
        <Typography variant="body2">
          Something went wrong fetching gift cards. Try again in a moment — if it keeps happening,
          contact your Trove representative.
        </Typography>
      </Alert>
    </Box>
  );
};

export default GiftcardListError;
